/**
 * F3 performance overlay — the "net graph". Answers one question when a player
 * says "it lags": which of the four layers is stuttering — the display loop,
 * the snapshot stream, the interpolation clock, or prediction?
 *
 * Everything is measured over a rolling window and redrawn twice a second into
 * a single fixed <pre>. Imperative DOM by design, like the touch joystick:
 * this runs inside the frame loop and must never involve React. Toggled with
 * F3, or forced on with `?debug` in the URL (phones have no F3).
 */

const WINDOW_MS = 5000;
const REDRAW_MS = 500;

interface DiagSources {
  /** Smoothed transport round-trip, ms. */
  rttMs(): number;
  /**
   * Interpolation counters since the previous call: frames a remote player had
   * no fresh sample to render (freeze-then-jump), and how far the clock
   * estimate snapped downward (forward time jumps), ms.
   */
  interp(): { starvedFrames: number; snapDownMs: number };
}

export class DiagOverlay {
  private element: HTMLPreElement | null = null;
  private visible = false;

  private frameGaps: number[] = [];
  private snapshotGaps: number[] = [];
  private reconcileErrors: number[] = [];
  private lastFrameAtMs = 0;
  private lastSnapshotAtMs = 0;
  private lastRedrawAtMs = 0;
  private starvedFrames = 0;
  private snapDownMs = 0;

  private readonly onKeyDown = (event: KeyboardEvent) => {
    if (event.code !== 'F3') return;
    event.preventDefault();
    this.toggle();
  };

  constructor(private readonly sources: DiagSources) {}

  attach(): void {
    window.addEventListener('keydown', this.onKeyDown);
    if (new URLSearchParams(location.search).has('debug')) this.toggle();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.element?.remove();
    this.element = null;
  }

  /** Every rAF frame. Also drives the redraw so there is no extra timer. */
  frame(nowMs: number): void {
    if (this.lastFrameAtMs !== 0) this.push(this.frameGaps, nowMs, nowMs - this.lastFrameAtMs);
    this.lastFrameAtMs = nowMs;
    if (!this.visible || nowMs - this.lastRedrawAtMs < REDRAW_MS) return;
    this.lastRedrawAtMs = nowMs;
    this.redraw(nowMs);
  }

  /** Every server snapshot, with the local prediction error it revealed. */
  snapshot(nowMs: number, reconcileErrorM: number): void {
    if (this.lastSnapshotAtMs !== 0) {
      this.push(this.snapshotGaps, nowMs, nowMs - this.lastSnapshotAtMs);
    }
    this.lastSnapshotAtMs = nowMs;
    this.push(this.reconcileErrors, nowMs, reconcileErrorM);
  }

  private toggle(): void {
    this.visible = !this.visible;
    if (!this.visible) {
      this.element?.remove();
      this.element = null;
      return;
    }
    const el = document.createElement('pre');
    el.style.cssText =
      'position:fixed;top:8px;right:8px;z-index:9999;margin:0;padding:6px 10px;' +
      'background:rgba(5,8,15,0.8);color:#9fe08a;font:11px/1.5 ui-monospace,monospace;' +
      'pointer-events:none;border-radius:6px;white-space:pre;';
    document.body.appendChild(el);
    this.element = el;
  }

  /** Values are kept as [timestamp, value] pairs and expired lazily. */
  private push(list: number[], nowMs: number, value: number): void {
    list.push(nowMs, value);
    while (list.length > 0 && nowMs - list[0]! > WINDOW_MS) list.splice(0, 2);
  }

  private redraw(nowMs: number): void {
    if (!this.element) return;
    const interp = this.sources.interp();
    this.starvedFrames += interp.starvedFrames;
    this.snapDownMs += interp.snapDownMs;

    const frames = this.frameGaps.length / 2;
    const seconds = Math.min(WINDOW_MS, nowMs - (this.frameGaps[0] ?? nowMs)) / 1000 || 1;
    const fps = frames / seconds;
    const snapsPerSec = this.snapshotGaps.length / 2 / seconds;

    this.element.textContent = [
      `fps ${fps.toFixed(0)}  frame p95 ${this.pct(this.frameGaps, 0.95).toFixed(1)}ms  worst ${this.max(this.frameGaps).toFixed(0)}ms`,
      `snapshots ${snapsPerSec.toFixed(0)}/s  gap p95 ${this.pct(this.snapshotGaps, 0.95).toFixed(0)}ms  worst ${this.max(this.snapshotGaps).toFixed(0)}ms`,
      `interp starved ${this.starvedFrames}f  clock snaps ${this.snapDownMs.toFixed(0)}ms  rtt ${this.sources.rttMs().toFixed(0)}ms`,
      `predict err p95 ${(this.pct(this.reconcileErrors, 0.95) * 100).toFixed(0)}cm  worst ${(this.max(this.reconcileErrors) * 100).toFixed(0)}cm`,
    ].join('\n');
  }

  private max(list: number[]): number {
    let out = 0;
    for (let i = 1; i < list.length; i += 2) out = Math.max(out, list[i]!);
    return out;
  }

  private pct(list: number[], p: number): number {
    const values: number[] = [];
    for (let i = 1; i < list.length; i += 2) values.push(list[i]!);
    if (values.length === 0) return 0;
    values.sort((a, b) => a - b);
    return values[Math.min(values.length - 1, Math.floor(values.length * p))]!;
  }
}
